import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import TopMenu from '../../../../TopMenu';

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  process.env.REACT_APP_API_BASE_URL ||
  'https://localhost:7038';

function parseStyle(json) {
  if (!json) return {};
  try {
    return JSON.parse(json);
  } catch {
    return {};
  }
}

function resolveAssetUrl(path) {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  return `${API_BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;
}

function normalise(t) {
  const cfg = parseStyle(t.styleConfigJson);

  return {
    id: t.id ?? t.slug,
    slug: t.slug ?? t.id,
    name: t.name ?? 'Untitled Template',
    accentColor: t.accentColor ?? cfg.accentColor ?? '#0d9488',
    sidebarBgColor: cfg.sidebarBgColor ?? t.sidebarBgColor ?? t.accentColor ?? '#0d9488',
    layout: t.layout ?? cfg.layout ?? 'single-column',
    thumbnailUrl: t.thumbnailUrl ?? null,
    styleConfigJson: t.styleConfigJson ?? null,
  };
}

function TemplateFallback({ tpl }) {
  const accent = tpl.accentColor || '#0d9488';
  const hasSidebar =
    tpl.layout === 'sidebar-left' ||
    tpl.layout === 'sidebar-right' ||
    tpl.layout === 'two-column';

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        borderRadius: 12,
        background: '#fff',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: hasSidebar ? 'row' : 'column',
      }}
    >
      {hasSidebar && (
        <div
          style={{
            width: '30%',
            background: tpl.sidebarBgColor || '#e5e7eb',
            padding: 10,
          }}
        >
          <div style={{ width: 34, height: 34, borderRadius: '50%', background: '#ffffff66', marginBottom: 10 }} />
          <div style={{ height: 6, background: '#ffffff99', borderRadius: 999, marginBottom: 6 }} />
          <div style={{ height: 6, width: '70%', background: '#ffffff88', borderRadius: 999, marginBottom: 12 }} />
          <div style={{ height: 5, background: '#ffffff70', borderRadius: 999, marginBottom: 5 }} />
          <div style={{ height: 5, width: '75%', background: '#ffffff70', borderRadius: 999 }} />
        </div>
      )}

      <div style={{ flex: 1, padding: 12 }}>
        {!hasSidebar && (
          <div
            style={{
              height: 34,
              borderRadius: 8,
              background: `linear-gradient(135deg, ${accent}, ${accent}cc)`,
              marginBottom: 12,
            }}
          />
        )}

        <div style={{ height: 8, width: '60%', background: '#11182722', borderRadius: 999, marginBottom: 8 }} />
        <div style={{ height: 6, width: '42%', background: '#11182718', borderRadius: 999, marginBottom: 14 }} />

        {[1, 2, 3].map((x) => (
          <div key={x} style={{ marginBottom: 12 }}>
            <div
              style={{
                height: 6,
                width: '30%',
                borderRadius: 999,
                background: accent,
                opacity: 0.8,
                marginBottom: 8,
              }}
            />
            <div style={{ height: 5, width: '100%', background: '#11182714', borderRadius: 999, marginBottom: 5 }} />
            <div style={{ height: 5, width: '82%', background: '#11182714', borderRadius: 999 }} />
          </div>
        ))}
      </div>
    </div>
  );
}

const StepTemplateSelect = ({
  onSelect,
  onBack,
  experience,
  industry,
  theme,
  toggleTheme,
}) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedSlug, setSelectedSlug] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const fetchTemplates = async () => {
      setLoading(true);
      setError('');
      setTemplates([]);
      setSelectedSlug('');

      try {
        const qs = industry ? `?industry=${encodeURIComponent(industry)}` : '';
        const res = await fetch(`${API_BASE_URL}/api/resume-builder/templates${qs}`);

        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }

        const data = await res.json();
        if (cancelled) return;

        const raw = Array.isArray(data) ? data : data.templates ?? [];
        const list = raw.map(normalise);

        setTemplates(list);
        setSelectedSlug(list[0]?.slug || '');

        if (list.length === 0) {
          setError(industry ? `No templates found for "${industry}".` : 'No templates available.');
        }
      } catch (e) {
        if (!cancelled) {
          console.error('Template fetch failed:', e);
          setError('Could not load templates from server.');
          setTemplates([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchTemplates();

    return () => {
      cancelled = true;
    };
  }, [industry]);

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.slug === selectedSlug) ?? null,
    [templates, selectedSlug]
  );

  const continueWithTemplate = (tpl) => {
    if (!tpl) return;
    setSelectedSlug(tpl.slug);

    if (experience === 'experienced') {
      setShowModal(true);
      return;
    }

    onSelect({
      templateId: tpl.slug,
      styleConfigJson: tpl.styleConfigJson,
      parsedData: null,
    });
  };

  const handleParse = async () => {
    if (!selectedTemplate) return;

    if (!file) {
      onSelect({
        templateId: selectedTemplate.slug,
        styleConfigJson: selectedTemplate.styleConfigJson,
        parsedData: null,
      });
      return;
    }

    setParsing(true);
    try {
      const fd = new FormData();
      fd.append('file', file);

      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/resume-builder/parse-upload`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });

      let parsedData = null;
      if (res.ok) {
        const result = await res.json();
        parsedData =
          result?.parsedData ??
          result?.data ??
          result?.formData ??
          null;
      }

      onSelect({
        templateId: selectedTemplate.slug,
        styleConfigJson: selectedTemplate.styleConfigJson,
        parsedData,
      });
    } catch (err) {
      console.error('Parse failed:', err);
      onSelect({
        templateId: selectedTemplate.slug,
        styleConfigJson: selectedTemplate.styleConfigJson,
        parsedData: null,
      });
    } finally {
      setParsing(false);
    }
  };

  const handleSkip = () => {
    if (!selectedTemplate) return;

    onSelect({
      templateId: selectedTemplate.slug,
      styleConfigJson: selectedTemplate.styleConfigJson,
      parsedData: null,
    });
  };

  return (
    <div className={`sel-shell ${theme || 'dark'}`}>
      <TopMenu theme={theme} toggleTheme={toggleTheme} />

      <div
        style={{
          maxWidth: 1280,
          margin: '0 auto',
          padding: '128px 20px 80px',
        }}
      >
        <div style={{ marginBottom: 24 }}>
          <h1
            style={{
              margin: 0,
              fontSize: 28,
              fontWeight: 800,
              color: theme === 'dark' ? '#F8FAFC' : '#111827',
            }}
          >
            Choose Resume Template
          </h1>
          {!!error && (
            <p style={{ marginTop: 8, color: '#f87171', fontSize: 13 }}>
              {error}
            </p>
          )}
        </div>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0' }}>
            <div className="rb-spinner" />
          </div>
        ) : templates.length === 0 ? (
          <div
            style={{
              padding: 32,
              borderRadius: 16,
              textAlign: 'center',
              border: theme === 'dark'
                ? '1px solid rgba(255,255,255,0.08)'
                : '1px solid #E5E7EB',
            }}
          >
            <h3 style={{ margin: 0, color: theme === 'dark' ? '#F8FAFC' : '#111827' }}>
              No templates available
            </h3>
          </div>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(60px, 1fr))',
              gap: 24,
            }}
          >
            {templates.map((tpl) => {
              const imageUrl = resolveAssetUrl(tpl.thumbnailUrl);
              const isSelected = selectedSlug === tpl.slug;

              return (
                <motion.div
                  key={tpl.slug}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25 }}
                  onClick={() => setSelectedSlug(tpl.slug)}
                  style={{
                    borderRadius: 18,
                    padding: 14,
                    cursor: 'pointer',
                    background: theme === 'dark' ? '#111827' : '#ffffff',
                    border: isSelected
                      ? `2px solid ${tpl.accentColor}`
                      : theme === 'dark'
                        ? '1px solid rgba(255,255,255,0.08)'
                        : '1px solid #E5E7EB',
                    boxShadow: '0 10px 24px rgba(0,0,0,0.08)',
                  }}
                >
                  <div
                    style={{
                      width: '100%',
                      aspectRatio: '3 / 4',
                      borderRadius: 12,
                      overflow: 'hidden',
                      background: '#f8fafc',
                      marginBottom: 14,
                    }}
                  >
                    {imageUrl ? (
                      <img
                        src={imageUrl}
                        alt={tpl.name}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          display: 'block',
                        }}
                      />
                    ) : (
                      <TemplateFallback tpl={tpl} />
                    )}
                  </div>

                  <div
                    style={{
                      textAlign: 'center',
                      fontWeight: 700,
                      fontSize: 15,
                      marginBottom: 12,
                      color: theme === 'dark' ? '#F8FAFC' : '#111827',
                    }}
                  >
                    {tpl.name}
                  </div>

                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      continueWithTemplate(tpl);
                    }}
                    style={{
                      width: '100%',
                      border: 'none',
                      borderRadius: 10,
                      padding: '11px 14px',
                      background: tpl.accentColor || '#0d9488',
                      color: '#fff',
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    Select
                  </button>
                </motion.div>
              );
            })}
          </div>
        )}

        <div
          style={{
            marginTop: 28,
            display: 'flex',
            justifyContent: 'flex-start',
          }}
        >
          <button
            type="button"
            onClick={onBack}
            style={{
              padding: '11px 16px',
              borderRadius: 10,
              background: 'transparent',
              border: theme === 'dark'
                ? '1px solid rgba(255,255,255,0.14)'
                : '1px solid #D1D5DB',
              color: theme === 'dark' ? '#E5E7EB' : '#111827',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            ← Back
          </button>
        </div>
      </div>

      <AnimatePresence>
        {showModal && (
          <motion.div
            className="parse-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="parse-modal"
              initial={{ opacity: 0, scale: 0.92, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.92, y: 20 }}
              transition={{ type: 'spring', stiffness: 300, damping: 26 }}
            >
              <button className="parse-close" onClick={() => setShowModal(false)}>✕</button>

              <div className="parse-icon">📄</div>
              <h2 className="parse-title">Import your existing resume</h2>
              <p className="parse-sub">Upload your CV and we&apos;ll auto-fill the form.</p>

              <div
                className={`parse-drop-zone ${file ? 'has-file' : ''}`}
                onClick={() => fileRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  const f = e.dataTransfer.files[0];
                  if (f) setFile(f);
                }}
              >
                <input
                  ref={fileRef}
                  type="file"
                  accept=".pdf,.doc,.docx"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) setFile(f);
                  }}
                />

                {file ? (
                  <>
                    <div className="parse-file-icon">✅</div>
                    <p className="parse-file-name">{file.name}</p>
                    <p className="parse-file-hint">Click to change file</p>
                  </>
                ) : (
                  <>
                    <div className="parse-upload-icon">⬆</div>
                    <p className="parse-drop-text">
                      Drag & drop or <span>browse</span>
                    </p>
                    <p className="parse-drop-hint">PDF, DOC, or DOCX — max 5 MB</p>
                  </>
                )}
              </div>

              <div className="parse-actions">
                <button className="parse-skip-btn" onClick={handleSkip}>
                  Skip — fill manually
                </button>
                <button
                  className={`parse-import-btn ${file ? 'enabled' : ''}`}
                  onClick={handleParse}
                  disabled={parsing}
                >
                  {parsing
                    ? 'Parsing...'
                    : file
                      ? 'Import & Continue →'
                      : 'Continue without import →'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default StepTemplateSelect;
